import React, { useState, useCallback, useEffect } from 'react';
import { Upload, X, Image, Loader2, Palette, Sliders, Info, CheckCircle2, AlertCircle, FileText, FileImage, Save, FolderOpen, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { withBackend } from '../utils/api';
import { toast } from 'sonner';

const MAX_REFERENCE_IMAGES = 14;

export default function BrandReferenceUpload({
  onReferencesChange,
  initialReferences = [],
  initialWeight = 'medium',
  userId = null,
  compact = false // When true, renders without Card wrapper
}) {
  const [referenceImages, setReferenceImages] = useState(initialReferences);
  const [referenceWeight, setReferenceWeight] = useState(initialWeight); // 'low', 'medium', 'high'
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [extractingFile, setExtractingFile] = useState(null); // Track file being extracted
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSavedReferences, setHasSavedReferences] = useState(false);

  // Load saved references on mount
  useEffect(() => {
    if (userId) {
      loadSavedReferences();
    }
  }, [userId]);

  const loadSavedReferences = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await fetch(withBackend(`/get-brand-references/${userId}`));
      if (response.ok) {
        const data = await response.json();
        if (data.references && data.references.length > 0) {
          setHasSavedReferences(true);
          // Don't auto-load - just mark that saved references exist
        }
      }
    } catch (error) {
      console.error('Failed to check saved references:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSavedReferences = async () => {
    if (!userId) {
      toast.error('Please sign in to load saved references');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(withBackend(`/get-brand-references/${userId}`));
      if (!response.ok) throw new Error('Failed to load references');

      const data = await response.json();
      if (data.references && data.references.length > 0) {
        const loadedImages = data.references.map((base64, index) => ({
          id: `saved-${Date.now()}-${index}`,
          name: `Saved Reference ${index + 1}`,
          base64: base64,
          size: 0,
          type: 'image/png',
          source: 'saved'
        }));

        setReferenceImages(loadedImages);
        setReferenceWeight(data.weight || 'medium');

        if (onReferencesChange) {
          onReferencesChange({
            images: loadedImages,
            weight: data.weight || 'medium'
          });
        }

        toast.success(`Loaded ${data.count} saved brand references`);
      } else {
        toast.error('No saved references found');
      }
    } catch (error) {
      console.error('Failed to load references:', error);
      toast.error('Failed to load saved references');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReferences = async () => {
    if (!userId) {
      toast.error('Please sign in to save references');
      return;
    }

    if (referenceImages.length === 0) {
      toast.error('No references to save');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(withBackend('/save-brand-references'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          references: referenceImages.map(img => img.base64),
          weight: referenceWeight
        })
      });

      if (!response.ok) throw new Error('Failed to save references');

      const data = await response.json();
      setHasSavedReferences(true);
      toast.success(`Saved ${data.count} brand references`);
    } catch (error) {
      console.error('Failed to save references:', error);
      toast.error('Failed to save references');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSavedReferences = async () => {
    if (!userId) return;

    try {
      const response = await fetch(withBackend(`/delete-brand-references/${userId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        setHasSavedReferences(false);
        toast.success('Saved references deleted');
      }
    } catch (error) {
      console.error('Failed to delete references:', error);
      toast.error('Failed to delete saved references');
    }
  };

  // Weight labels and descriptions
  const weightOptions = {
    low: {
      label: 'Low',
      value: 0.3,
      description: 'Loosely inspired by brand references',
      color: 'text-blue-500'
    },
    medium: {
      label: 'Medium',
      value: 0.6,
      description: 'Balanced brand adherence',
      color: 'text-yellow-500'
    },
    high: {
      label: 'High',
      value: 0.9,
      description: 'Strictly follow brand style',
      color: 'text-red-500'
    }
  };

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Extract slides from PPTX or PDF via backend
  const extractSlidesFromFile = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const remainingSlots = MAX_REFERENCE_IMAGES - referenceImages.length;
    formData.append('max_slides', Math.min(remainingSlots, MAX_REFERENCE_IMAGES).toString());

    setExtractingFile(file.name);

    try {
      const response = await fetch(withBackend('/extract-brand-references'), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to extract slides');
      }

      const data = await response.json();

      if (data.slides && data.slides.length > 0) {
        const newImages = data.slides.map((slideBase64, index) => ({
          id: `ref-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: `${file.name} - Slide ${index + 1}`,
          base64: slideBase64.startsWith('data:') ? slideBase64 : `data:image/png;base64,${slideBase64}`,
          size: 0,
          type: 'image/png',
          source: file.name
        }));

        const updatedImages = [...referenceImages, ...newImages].slice(0, MAX_REFERENCE_IMAGES);
        setReferenceImages(updatedImages);

        if (onReferencesChange) {
          onReferencesChange({
            images: updatedImages,
            weight: referenceWeight
          });
        }

        toast.success(`Extracted ${newImages.length} slides from ${file.name}`);
      } else {
        toast.error('No slides found in file');
      }
    } catch (error) {
      console.error('Error extracting slides:', error);
      toast.error(error.message || 'Failed to extract slides from file');
    } finally {
      setExtractingFile(null);
    }
  }, [referenceImages, referenceWeight, onReferencesChange]);

  // Handle file selection
  const handleFiles = useCallback(async (files) => {
    const fileArray = Array.from(files);

    // Separate files by type
    const imageFiles = fileArray.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isValidType = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type);
      return isImage && isValidType;
    });

    const documentFiles = fileArray.filter(file => {
      const isPptx = file.name.toLowerCase().endsWith('.pptx') ||
                     file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      const isPdf = file.name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
      return isPptx || isPdf;
    });

    // Process document files (PPTX/PDF) via backend
    for (const docFile of documentFiles) {
      await extractSlidesFromFile(docFile);
    }

    // Process image files directly
    if (imageFiles.length === 0 && documentFiles.length === 0) {
      toast.error('Please upload PNG, JPG, PPTX, or PDF files');
      return;
    }

    if (imageFiles.length === 0) return; // Only document files were uploaded

    const remainingSlots = MAX_REFERENCE_IMAGES - referenceImages.length;
    if (imageFiles.length > remainingSlots) {
      toast.error(`Can only add ${remainingSlots} more images (max ${MAX_REFERENCE_IMAGES})`);
      imageFiles.splice(remainingSlots);
    }

    if (imageFiles.length === 0) return;

    setUploading(true);

    try {
      const newImages = await Promise.all(
        imageFiles.map(async (file) => {
          const base64 = await fileToBase64(file);
          return {
            id: `ref-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: file.name,
            base64: base64,
            size: file.size,
            type: file.type
          };
        })
      );

      const updatedImages = [...referenceImages, ...newImages];
      setReferenceImages(updatedImages);

      // Notify parent component
      if (onReferencesChange) {
        onReferencesChange({
          images: updatedImages,
          weight: referenceWeight
        });
      }

      toast.success(`Added ${newImages.length} brand reference image(s)`);
    } catch (error) {
      console.error('Error processing images:', error);
      toast.error('Failed to process images');
    } finally {
      setUploading(false);
    }
  }, [referenceImages, referenceWeight, onReferencesChange, extractSlidesFromFile]);

  // Handle drag and drop
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  // Remove an image
  const removeImage = useCallback((imageId) => {
    const updatedImages = referenceImages.filter(img => img.id !== imageId);
    setReferenceImages(updatedImages);

    if (onReferencesChange) {
      onReferencesChange({
        images: updatedImages,
        weight: referenceWeight
      });
    }
  }, [referenceImages, referenceWeight, onReferencesChange]);

  // Handle weight change
  const handleWeightChange = useCallback((newWeight) => {
    setReferenceWeight(newWeight);

    if (onReferencesChange) {
      onReferencesChange({
        images: referenceImages,
        weight: newWeight
      });
    }
  }, [referenceImages, onReferencesChange]);

  // Clear all images
  const clearAll = useCallback(() => {
    setReferenceImages([]);
    if (onReferencesChange) {
      onReferencesChange({
        images: [],
        weight: referenceWeight
      });
    }
    toast.success('All brand references cleared');
  }, [referenceWeight, onReferencesChange]);

  // Compact content (for inline use within another Card)
  const compactContent = (
    <div className="space-y-4">
      {/* Compact header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">Upload reference slides</p>
          {referenceImages.length > 0 && (
            <Badge variant="secondary" className="bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300 text-xs">
              {referenceImages.length}/{MAX_REFERENCE_IMAGES}
            </Badge>
          )}
        </div>
        {userId && hasSavedReferences && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleLoadSavedReferences}
            disabled={loading}
            className="text-purple-600 border-purple-300 hover:bg-purple-50 h-7 text-xs"
          >
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FolderOpen className="w-3 h-3 mr-1" />}
            Load Saved
          </Button>
        )}
      </div>

      {/* Compact upload area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-all ${
          dragActive
            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
            : 'border-slate-300 dark:border-slate-600 hover:border-purple-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept="image/*,.pdf,.pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading || referenceImages.length >= MAX_REFERENCE_IMAGES}
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
            <span className="text-sm text-muted-foreground">Processing...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1">
            <Upload className="w-6 h-6 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              Drop images, PDFs, or PPTX files here
            </p>
          </div>
        )}
      </div>

      {/* Compact image grid */}
      {referenceImages.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {referenceImages.map((img, index) => (
            <div key={img.id} className="relative group aspect-video rounded-lg overflow-hidden border bg-slate-100 dark:bg-slate-800">
              <img
                src={img.base64}
                alt={img.name}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => removeImage(img.id)}
                className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Compact weight selector */}
      {referenceImages.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Influence:</span>
          <div className="flex gap-1">
            {['low', 'medium', 'high'].map((w) => (
              <button
                key={w}
                onClick={() => {
                  setReferenceWeight(w);
                  if (onReferencesChange) {
                    onReferencesChange({ images: referenceImages, weight: w });
                  }
                }}
                className={`px-2 py-0.5 text-xs rounded-full transition-all ${
                  referenceWeight === w
                    ? 'bg-purple-500 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200'
                }`}
              >
                {w.charAt(0).toUpperCase() + w.slice(1)}
              </button>
            ))}
          </div>
          {userId && referenceImages.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSaveReferences}
              disabled={saving}
              className="ml-auto h-6 text-xs text-green-600 hover:text-green-700"
            >
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
              Save
            </Button>
          )}
        </div>
      )}
    </div>
  );

  // Return compact content if compact mode
  if (compact) {
    return compactContent;
  }

  // Full Card version
  return (
    <Card className="border-2 border-dashed border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50/50 to-indigo-50/50 dark:from-purple-900/10 dark:to-indigo-900/10">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Palette className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <CardTitle className="text-lg">Brand Reference Slides</CardTitle>
              <CardDescription>
                Upload up to {MAX_REFERENCE_IMAGES} slides to guide the visual style
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {referenceImages.length > 0 && (
              <Badge variant="secondary" className="bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300">
                {referenceImages.length}/{MAX_REFERENCE_IMAGES} images
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Save/Load Controls */}
        {userId && (
          <div className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border">
            <div className="flex-1">
              <p className="text-sm font-medium">Saved Brand References</p>
              <p className="text-xs text-muted-foreground">
                {hasSavedReferences ? 'You have saved references available' : 'Save your references for quick reuse'}
              </p>
            </div>
            <div className="flex gap-2">
              {hasSavedReferences && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLoadSavedReferences}
                    disabled={loading}
                    className="text-purple-600 border-purple-300 hover:bg-purple-50"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <FolderOpen className="w-4 h-4 mr-1" />
                    )}
                    Load
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDeleteSavedReferences}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
              {referenceImages.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveReferences}
                  disabled={saving}
                  className="text-green-600 border-green-300 hover:bg-green-50"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  Save
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Info Banner */}
        <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Upload existing brand slides (PNG, JPG, PPTX, or PDF). The AI will analyze the color palette,
            typography, and layout to apply the same style to generated slides.
          </p>
        </div>

        {/* Reference Weight Slider */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium flex items-center gap-2">
              <Sliders className="w-4 h-4" />
              Reference Weight
            </label>
            <span className={`text-sm font-semibold ${weightOptions[referenceWeight].color}`}>
              {weightOptions[referenceWeight].label}
            </span>
          </div>

          <div className="flex gap-2">
            {Object.entries(weightOptions).map(([key, option]) => (
              <Button
                key={key}
                variant={referenceWeight === key ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleWeightChange(key)}
                className={`flex-1 ${referenceWeight === key ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
              >
                {option.label}
              </Button>
            ))}
          </div>

          <p className="text-xs text-muted-foreground">
            {weightOptions[referenceWeight].description}
          </p>
        </div>

        {/* Upload Area */}
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 transition-all ${
            dragActive
              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
              : 'border-gray-300 dark:border-gray-700 hover:border-purple-400'
          } ${referenceImages.length >= MAX_REFERENCE_IMAGES ? 'opacity-50 pointer-events-none' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            multiple
            accept="image/png,image/jpeg,image/jpg,image/webp,.pptx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation"
            onChange={(e) => handleFiles(e.target.files)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={referenceImages.length >= MAX_REFERENCE_IMAGES || uploading || extractingFile}
          />

          <div className="flex flex-col items-center justify-center text-center">
            {extractingFile ? (
              <>
                <Loader2 className="w-10 h-10 text-purple-500 animate-spin mb-3" />
                <p className="text-sm text-muted-foreground">Extracting slides from {extractingFile}...</p>
              </>
            ) : uploading ? (
              <>
                <Loader2 className="w-10 h-10 text-purple-500 animate-spin mb-3" />
                <p className="text-sm text-muted-foreground">Processing images...</p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <Upload className="w-8 h-8 text-purple-400" />
                  <FileText className="w-6 h-6 text-purple-300" />
                  <FileImage className="w-6 h-6 text-purple-300" />
                </div>
                <p className="text-sm font-medium text-foreground mb-1">
                  Drop brand slides here or click to browse
                </p>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG, PPTX, or PDF (up to 10MB each)
                </p>
              </>
            )}
          </div>
        </div>

        {/* Image Grid */}
        {referenceImages.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Uploaded References</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
              >
                <X className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            </div>

            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 gap-3">
              {referenceImages.map((image, index) => (
                <div
                  key={image.id}
                  className="relative group aspect-video bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700"
                >
                  <img
                    src={image.base64}
                    alt={`Reference ${index + 1}`}
                    className="w-full h-full object-cover"
                  />

                  {/* Overlay with remove button */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      onClick={() => removeImage(image.id)}
                      className="p-1.5 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Index badge */}
                  <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-black/60 rounded text-white text-xs font-medium">
                    {index + 1}
                  </div>
                </div>
              ))}

              {/* Add more placeholder */}
              {referenceImages.length < MAX_REFERENCE_IMAGES && (
                <label className="aspect-video border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center cursor-pointer hover:border-purple-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg,image/jpg,image/webp,.pptx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                    onChange={(e) => handleFiles(e.target.files)}
                    className="hidden"
                    disabled={extractingFile}
                  />
                  {extractingFile ? (
                    <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                  ) : (
                    <Plus className="w-6 h-6 text-gray-400" />
                  )}
                </label>
              )}
            </div>
          </div>
        )}

        {/* Status indicator */}
        {referenceImages.length > 0 && (
          <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            <p className="text-sm text-green-700 dark:text-green-300">
              {referenceImages.length} brand reference{referenceImages.length !== 1 ? 's' : ''} will be used with <strong>{weightOptions[referenceWeight].label.toLowerCase()}</strong> influence
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Plus icon component (if not imported)
function Plus({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}
